/**
 * Login page — /login
 *
 * The primary entry point for all platform services. Services redirect here with:
 *   /login?redirect_uri=https://app.abacus.dev/dashboard
 *
 * After successful authentication, redirects to redirect_uri.
 * Falls back to the platform home if no redirect_uri is provided.
 *
 * Auth method: passkey (featured) or email/password via Better-Auth, OAuth as alternate.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@better-bookkeeping/ui/button";
import { Input } from "@better-bookkeeping/ui/input";
import { Label } from "@better-bookkeeping/ui/label";
import { Alert, AlertDescription } from "@better-bookkeeping/ui/alert";
import { PasswordInput } from "@better-bookkeeping/ui/password-input";
import { AlertCircle, Fingerprint } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { authClient } from "@/lib/auth-client";
import { safePostAuthRedirect, safeRedirectHostname } from "@/lib/redirect-utils";
import { navigateToFinalDestination } from "@/lib/post-auth-redirect";
import { useRuntimeConfig } from "@/lib/runtime-config";
import { GoogleIcon } from "@/components/google-icon";
import { classifyPasskeyError } from "@/lib/passkey-errors";
import { getSocialSignInError } from "@/lib/social-sign-in-error";
import { buildGatewayCallbackURL } from "@/lib/auth-flow-redirects";
import {
  getPendingInvitation,
  preparePendingInvitationAuth,
  withInviteTokenSearch,
} from "@/lib/pending-pricing-invite";

export const Route = createFileRoute("/login")({
  validateSearch: withInviteTokenSearch({
    redirect_uri: z.string().optional(),
  }).parse,
  component: LoginPage,
});

// Exported for unit tests, matching SecurityPage in ./security.
export function LoginPage() {
  const { redirect_uri } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const runtimeConfig = useRuntimeConfig();
  const [error, setError] = useState<string | null>(null);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const redirectHost = redirect_uri ? safeRedirectHostname(redirect_uri, runtimeConfig) : null;
  const isInvitation = getPendingInvitation() !== null;

  const form = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    onSubmit: async ({ value }) => {
      try {
        setError(null);

        // AUTH-132: route through gateway "/" so the post-sign-in decision
        // tree happens in one place — index.tsx checks factor enrollment
        // and mfaSatisfied to route to /step-up, /enroll-mfa, or
        // the original destination.
        const prepared = await preparePendingInvitationAuth(undefined);
        const callbackURL = buildGatewayCallbackURL(
          runtimeConfig,
          redirect_uri,
          prepared.continuationId,
        );

        // AUTH-104: rememberMe=false → non-persistent session cookie.
        // Users opt into 30-day persistence on the post-2FA/passkey step
        // via auth.rememberDevice(), not from the password form.
        const { data, error: signInError } = await authClient.signIn.email({
          email: value.email,
          password: value.password,
          callbackURL,
          rememberMe: false,
        });

        if (signInError) {
          if (signInError.status === 429 || signInError.message?.includes("429")) {
            setError("Too many tries. Take a breath and try again in a minute.");
          } else if (signInError.code === "INVALID_EMAIL_OR_PASSWORD") {
            setError("That email and password don't match. Try again.");
          } else {
            setError("We couldn't sign you in. Try again.");
          }
          return;
        }

        // Better Auth's twoFactor plugin returns this for users with 2FA
        // enabled — go to /2fa, which on success flips mfaSatisfied via the
        // after-hook (AUTH-131) and continues to redirect_uri.
        const responseData = data as Record<string, unknown> | null;
        if (responseData?.twoFactorRedirect) {
          navigate({ to: "/2fa", search: { redirect_uri } });
          return;
        }

        // OAuth flows return a redirect URL — let the provider handle it.
        if (data?.url && data?.redirect) {
          window.location.href = data.url;
          return;
        }

        // Successful password sign-in without 2FA gating — bounce through
        // gateway "/" so the AUTH-132 decision tree applies.
        await queryClient.invalidateQueries({ queryKey: ["auth", "gateway-session"] });
        navigate({ to: "/", search: { redirect_uri } });
      } catch (err) {
        console.error("Login failed:", err);
        setError("Something went wrong. Please try again.");
      }
    },
  });

  return (
    <div className="space-y-6">
      <div className="space-y-1.5">
        <h2 className="text-2xl font-semibold tracking-tight" data-testid="login-title">
          Sign in
        </h2>
        {isInvitation ? (
          <p className="text-sm text-muted-foreground" data-testid="login-subtitle">
            Sign in to review your invitation. You'll confirm which account to use before choosing
            what to accept or decline.
          </p>
        ) : redirectHost ? (
          <p className="text-sm text-muted-foreground" data-testid="login-subtitle">
            Continuing to <strong className="text-foreground">{redirectHost}</strong>.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="login-subtitle">
            Welcome back to Visor.
          </p>
        )}
      </div>

      {error && (
        <Alert variant="destructive" data-testid="login-error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Featured: passkey. Same electric-subtle treatment used on the
          /security picker so users see "this is the recommended path"
          consistently across enrollment and sign-in. AUTH-104: passkey is
          inherently strong device binding — no separate "remember me". */}
      <div className="space-y-3">
        <button
          type="button"
          data-testid="login-passkey-button"
          disabled={passkeyLoading}
          onClick={async () => {
            try {
              setPasskeyLoading(true);
              setError(null);
              await preparePendingInvitationAuth(undefined);
              const { error: passkeyError } = await authClient.signIn.passkey();
              if (passkeyError) {
                setError(classifyPasskeyError(passkeyError).message);
                return;
              }
              await queryClient.invalidateQueries({ queryKey: ["auth", "gateway-session"] });
              // Passkey is a full second factor — go to the final destination
              // (with the AUTH-154 duplicate check applied inline).
              const target = safePostAuthRedirect(redirect_uri, runtimeConfig) ?? "/";
              await navigateToFinalDestination({
                target,
                navigate,
                runtimeConfig,
              });
            } catch (err) {
              setError(classifyPasskeyError(err).message);
            } finally {
              setPasskeyLoading(false);
            }
          }}
          className="block w-full rounded-lg border border-electric bg-electric-subtle p-4 text-left transition-colors hover:bg-electric/15 disabled:cursor-not-allowed disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-electric"
        >
          <div className="flex items-center gap-3">
            <Fingerprint className="h-5 w-5 shrink-0 text-electric" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-electric-ink">
                {passkeyLoading ? "Waiting for passkey…" : "Sign in with a passkey"}
              </p>
              <p className="text-xs text-electric-ink/80">
                Use your fingerprint, face, or device PIN.
              </p>
            </div>
          </div>
        </button>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          data-testid="login-google-button"
          onClick={async () => {
            setError(null);
            // AUTH-132: route OAuth callback through gateway "/" so the
            // single post-sign-in decision tree applies (factor enrollment
            // check, mfaSatisfied gate, redirect_uri).
            try {
              const prepared = await preparePendingInvitationAuth(undefined);
              const callbackURL = buildGatewayCallbackURL(
                runtimeConfig,
                redirect_uri,
                prepared.continuationId,
              );
              const { error: socialError, data } = await authClient.signIn.social({
                provider: "google",
                callbackURL,
              });
              if (socialError) {
                setError(getSocialSignInError(socialError));
                return;
              }
              // Better Auth returns { url, redirect } for OAuth — navigate manually
              // since we disabled the default fetch plugins
              if (data?.url && data?.redirect) {
                window.location.href = data.url;
              }
            } catch {
              setError(
                isInvitation
                  ? "Couldn't start sign-in. Reopen the invitation link and try again."
                  : "We couldn't start Google sign-in. Try again.",
              );
            }
          }}
        >
          <GoogleIcon className="mr-2 h-4 w-4" />
          Continue with Google
        </Button>
      </div>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-card px-2 text-muted-foreground">or with email</span>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        className="space-y-4"
        data-testid="login-form"
      >
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <form.Field
            name="email"
            validators={{
              onChange: z.string().email("Please enter a valid email"),
            }}
            children={(field) => (
              <div>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="login-email-input"
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive mt-1" data-testid="login-email-error">
                    {typeof field.state.meta.errors[0] === "string"
                      ? field.state.meta.errors[0]
                      : field.state.meta.errors[0]?.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link
              to="/forgot-password"
              search={{ redirect_uri }}
              className="text-sm text-primary hover:underline"
              data-testid="login-forgot-password-link"
            >
              Forgot password?
            </Link>
          </div>
          <form.Field
            name="password"
            validators={{
              onChange: z.string().min(1, "Password is required"),
            }}
            children={(field) => (
              <div>
                <PasswordInput
                  id="password"
                  autoComplete="current-password"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="••••••••••••"
                  required
                  data-testid="login-password-input"
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive mt-1" data-testid="login-password-error">
                    {typeof field.state.meta.errors[0] === "string"
                      ? field.state.meta.errors[0]
                      : field.state.meta.errors[0]?.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>

        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <Button
              type="submit"
              disabled={!canSubmit || isSubmitting}
              className="w-full"
              data-testid="login-submit-button"
            >
              {isSubmitting ? "Signing in…" : "Sign in"}
            </Button>
          )}
        />
      </form>

      <p className="text-center text-sm text-muted-foreground">
        New to Visor?{" "}
        <Link
          to="/signup"
          search={{ redirect_uri }}
          className="text-primary hover:underline font-medium"
          data-testid="login-signup-link"
        >
          Create an account
        </Link>
      </p>
    </div>
  );
}
